// Full-page in-app render — mirrors freestyler's injectGenerated (see
// smbls/plugins/freestyler/index.js ~L709-760) but runs client-side via
// el.call, dispatched from freestyleBridge.js when mode === 'app'.
//
// On a canvas_tool_call { tool:'render_component', args:{name,source} }
// this function:
//   1. Parses the DOMQL object-literal source string via parseFreestyleSource.
//   2. Finds the router root (walks el.parent for a node with .routes + .context).
//   3. Registers def.icons / def.components into ctx (mirrors freestyler).
//   4. Wraps def.onRender so any [data-back] nodes route to '/' (defensive).
//   5. Derives a unique slug-route from `name`, deduped against existing ctx.pages.
//   6. Registers the page on ctx.pages[route] and calls routerRoot.router(route).
//   7. Returns { ok:true, route } on success or { ok:false, error } for agent
//      self-correction (same contract as injectOnCanvas).
//
// Signature matches the el.call convention: `this` is the element when called
// via el.call('injectPageInApp', args). Also handles the direct-call case
// (first arg = el, second arg = args) for unit-test compatibility.

// Slugify a name string into a URL-safe path segment.
// Mirrors the pattern freestyler uses for routeKey: lowercase, words joined
// by '-', leading '/' prepended.
function _canvasSlugify(name) {
  return (
    '/' +
      (name || 'generated')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64) || 'generated'
  )
}

// Walk el.parent upward to find the first element that has BOTH .routes and
// .context — that is the router root. Mirrors freestyler's findRouterRoot.
// Falls back to el.getRoot() if the walk exhausts.
// SANDBOX (ISOLATED-RUNTIME-PLAN §8, flag appsSandbox, default off): the
// workspace decides; the def comes from its registered builder (canvas cannot
// import workspace). Flag on with no builder registered: fail closed.
const _sandboxOn = (el) => {
  const root = (el.getRootState && el.getRootState()) || (el.state && (el.state.root || el.state))
  return el.call('generatedAppSandboxOn', root) === true
}

function _findRouterRoot(el) {
  let node = el
  while (node) {
    if (node.routes && node.context) return node
    node = node.parent
  }
  return el.getRoot ? el.getRoot() : null
}

export function injectPageInApp({ name, source } = {}) {
  // el.call convention: `this` is bound to the element. Also accept the
  // direct-call signature (injectPageInApp(el, args)) for test compat.
  const el = this && this.getRoot ? this : arguments[0]
  const args = this && this.getRoot ? { name, source } : arguments[1] || {}
  const _name = args.name || name
  const _source = args.source || source

  if (!_name || !_source) {
    return { ok: false, error: 'name and source are required' }
  }

  // Parse the DOMQL object-literal source.
  // CONTAINMENT GATE (Nika 2026-10-01 "Cut access now") — the same AST gate
  // every generated-app path uses (workspace functions/
  // generatedAppContainment.js parseGeneratedAppSource, reached by el.call:
  // canvas may not import workspace modules). FAIL CLOSED: no gate, no parse.
  if (_sandboxOn(el)) {
    const built = el.call('buildSandboxedAppDef', { name: _name, source: _source })
    if (!built || typeof built !== 'object') {
      return { ok: false, error: 'sandbox builder unavailable — refusing to mount' }
    }
    if (built.blocked)
      return { ok: false, blocked: true, error: built.error, violations: built.violations }
    if (!built.ok) return { ok: false, error: built.error }
    return _routePage(el, _name, built.def, { sandboxed: true })
  }
  let parsed
  try {
    parsed = el.call('parseGeneratedAppSource', _source)
  } catch (e) {
    return { ok: false, error: 'parse: ' + ((e && e.message) || String(e)) }
  }
  if (!parsed || typeof parsed !== 'object') {
    return {
      ok: false,
      error: 'containment gate unavailable — refusing to evaluate unaudited source'
    }
  }
  if (parsed.blocked) {
    return { ok: false, blocked: true, error: parsed.error, violations: parsed.violations }
  }
  if (!parsed.ok) return { ok: false, error: parsed.error }
  const def = parsed.def
  if (!def || typeof def !== 'object') {
    return { ok: false, error: 'source did not evaluate to an object' }
  }

  return _routePage(el, _name, def, { sandboxed: false })
}

function _routePage(el, _name, def, { sandboxed }) {
  // Find the router root — the element that owns .routes + .context.
  const routerRoot = _findRouterRoot(el)
  if (!routerRoot) {
    return { ok: false, error: 'no router root found' }
  }

  const ctx = routerRoot.context
  if (!ctx) {
    return { ok: false, error: 'router root has no context' }
  }

  // Register icons — merge into ctx.designSystem.icons (skip existing keys).
  // Unlike injectOnCanvas, in-app rendering shares the same design system
  // context as the shell, so icon merging works here directly.
  if (def.icons && typeof def.icons === 'object') {
    if (!ctx.designSystem) ctx.designSystem = {}
    if (!ctx.designSystem.icons) ctx.designSystem.icons = {}
    for (const [k, v] of Object.entries(def.icons)) {
      if (!ctx.designSystem.icons[k]) ctx.designSystem.icons[k] = v
    }
  }

  // Register supplementary components from the agent's output.
  if (def.components && typeof def.components === 'object') {
    if (!ctx.components) ctx.components = {}
    for (const [k, v] of Object.entries(def.components)) {
      if (!ctx.components[k]) ctx.components[k] = v
    }
  }

  // The page definition is either def.page or the def itself (mirrors
  // freestyler's `const pageDef = result.page || result`).
  const pageDef = def.page || def

  // Wrap onRender so [data-back] nodes route back to '/' defensively,
  // mirroring freestyler L726-731. The agent is instructed to include a
  // back button but we make it work regardless of element naming.
  const originalOnRender = pageDef.onRender
  // a sandboxed page's DOM lives in its opaque frame: nothing to wire here
  if (!sandboxed)
    pageDef.onRender = function (pageEl, s) {
      // Wire any [data-back] nodes to navigate to '/'.
      try {
        if (pageEl && pageEl.node) {
          pageEl.node.querySelectorAll('[data-back]').forEach((btn) => {
            btn.addEventListener('click', (e) => {
              e.preventDefault()
              const rr = _findRouterRoot(pageEl)
              if (rr) rr.router('/', rr)
            })
          })
        }
      } catch (_) {}
      // Call the original onRender from the agent's output.
      if (typeof originalOnRender === 'function') originalOnRender(pageEl, s)
    }

  // Merge any top-level state from the agent's result.
  if (def.state && typeof def.state === 'object') {
    pageDef.state = { ...(pageDef.state || {}), ...def.state }
  }

  // Derive a unique route from the name. Dedupe '-2', '-3', ... against
  // existing ctx.pages (mirrors freestyler L750-756).
  if (!ctx.pages) ctx.pages = {}
  const baseRoute = _canvasSlugify(_name)
  let finalRoute = baseRoute
  let i = 2
  while (ctx.pages[finalRoute]) {
    finalRoute = baseRoute + '-' + i
    i++
  }

  // Register the page and navigate.
  ctx.pages[finalRoute] = pageDef
  try {
    routerRoot.router(finalRoute, routerRoot)
  } catch (err) {
    return { ok: false, error: 'router: ' + ((err && err.message) || String(err)) }
  }

  return { ok: true, route: finalRoute }
}
